// File: src/components/ExecutedTestcasesPage.jsx

import React, { useEffect, useState } from "react";
import axios from "../axios";   // ← baseURL is "/api"
import * as XLSX from "xlsx";

export default function ExecutedTestcasesPage() {
    // State
    const [projects, setProjects] = useState([]);
    const [suites, setSuites] = useState([]);
    const [executedTestcases, setExecutedTestcases] = useState([]);

    // Use null initial state for “no project selected”
    const [selectedProject, setSelectedProject] = useState(null);
    const [selectedSuite, setSelectedSuite] = useState("");
    const [isLoading, setIsLoading] = useState(false);

    const [search, setSearch] = useState("");
    const [filterStatus, setFilterStatus] = useState("");

    // 1) Fetch all projects on mount
    useEffect(() => {
        const fetchProjects = async () => {
            setIsLoading(true);
            try {
                // GET /api/projects → might return { success: true, projects: [...] } or a raw array [...]
                const res = await axios.get("/projects");
                const data = res.data;

                if (data.success && Array.isArray(data.projects)) {
                    setProjects(data.projects);
                } else if (Array.isArray(data)) {
                    setProjects(data);
                } else {
                    setProjects([]);
                }
            } catch (err) {
                console.error("Failed to fetch projects:", err);
                setProjects([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchProjects();
    }, []);

    // 2) Fetch suites whenever selectedProject changes
    useEffect(() => {
        // If no project is currently selected, clear out `suites`
        if (selectedProject === null) {
            setSuites([]);
            setSelectedSuite("");
            return;
        }

        const fetchSuites = async () => {
            setIsLoading(true);
            try {
                // GET /api/suites/:projectId → might return { success: true, suites: [...] } or raw array [...]
                const res = await axios.get(`/suites/${selectedProject}`);
                const data = res.data;

                let fetchedSuites = [];
                if (data.success && Array.isArray(data.suites)) {
                    fetchedSuites = data.suites;
                } else if (Array.isArray(data)) {
                    fetchedSuites = data;
                }

                console.log("Fetched suites for project ID", selectedProject, "→", fetchedSuites);
                setSuites(fetchedSuites);
            } catch (err) {
                console.error("Failed to load suites:", err);
                setSuites([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchSuites();
    }, [selectedProject]);

    // 3) Fetch executed testcases when both project & suite are chosen
    useEffect(() => {
        // Only proceed if user has selected both a project and a suite
        if (selectedProject === null || !selectedSuite || suites.length === 0) {
            setExecutedTestcases([]);
            return;
        }

        // Find the suite object whose suite_id matches selectedSuite
        const suiteObj = suites.find(
            (s) => String(s.suite_id) === String(selectedSuite)
        );
        if (!suiteObj) {
            setExecutedTestcases([]);
            return;
        }
        const suiteName = suiteObj.name; // e.g. "Login_KheloExch"

        const fetchExecutedTestcases = async () => {
            setIsLoading(true);
            try {
                // *** KEY CHANGE: PASS project ID (selectedProject), not projectName ***
                // GET /api/executed-testcases?project=<PROJECT_ID>&suite=<suiteName>
                const url = `/executed-testcases?project=${selectedProject}&suite=${encodeURIComponent(
                    suiteName
                )}`;

                console.log("Fetching executed-testcases from URL:", url);
                const res = await axios.get(url);
                const data = res.data;

                let resultsArray = [];
                if (data.success && Array.isArray(data.results)) {
                    resultsArray = data.results;
                } else if (Array.isArray(data)) {
                    resultsArray = data;
                }

                console.log(
                    "Fetched executed testcases for project ID",
                    selectedProject,
                    "suite",
                    suiteName,
                    "→",
                    resultsArray
                );
                setExecutedTestcases(resultsArray);
            } catch (err) {
                console.error("Failed to fetch executed testcases:", err);
                setExecutedTestcases([]);
            } finally {
                setIsLoading(false);
            }
        };

        fetchExecutedTestcases();
    }, [selectedProject, selectedSuite, suites]);

    // Filter executed testcases by search & status
    const filteredTestcases = executedTestcases.filter((tc) => {
        const matchesTitle = tc.title
            ?.toLowerCase()
            .includes(search.toLowerCase());
        const matchesStatus = filterStatus ? tc.status === filterStatus : true;
        return matchesTitle && matchesStatus;
    });

    // Export filtered list to Excel
    const exportToExcel = () => {
        const worksheet = XLSX.utils.json_to_sheet(filteredTestcases);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "ExecutedTestcases");
        XLSX.writeFile(workbook, "ExecutedTestcases.xlsx");
    };

    return (
        <div className="p-6">
            <h2 className="text-2xl font-bold mb-6">Executed Testcases</h2>

            {/* STEP 1: Project Selection as Cards */}
            {selectedProject === null ? (
                <section className="mb-6 bg-white p-4 rounded shadow">
                    <h3 className="text-xl font-semibold text-gray-700 mb-3">
                        Select a Project
                    </h3>
                    {isLoading ? (
                        <div className="text-center text-gray-500 py-4">
                            Loading projects…
                        </div>
                    ) : projects.length === 0 ? (
                        <div className="text-center text-gray-500 py-4">
                            No projects available
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                            {projects.map((proj) => {
                                // Determine which field really holds the ID:
                                const projectKey = proj.project_id ?? proj.id ?? proj._id;

                                return (
                                    <button
                                        key={projectKey}
                                        type="button"
                                        onClick={() => {
                                            console.log("Clicked project:", proj, "→ using ID:", projectKey);
                                            setSelectedProject(projectKey);
                                            setSelectedSuite("");
                                            setExecutedTestcases([]);
                                        }}
                                        className={`border rounded-lg p-2 text-left w-full transition-shadow ${
                                            String(selectedProject) === String(projectKey)
                                                ? "bg-indigo-100 border-indigo-400 shadow-md"
                                                : "bg-white border-gray-200 hover:shadow-lg"
                                        }`}
                                    >
                                        <p className="text-lg font-semibold text-indigo-700 truncate">
                                            {proj.project_name}
                                        </p>
                                    </button>
                                );
                            })}
                        </div>
                    )}
                </section>
            ) : (
                <section className="mb-6 bg-white p-4 rounded shadow flex items-center justify-between">
                    <p className="text-lg text-gray-800">
                        Project:{" "}
                        <span className="font-semibold text-indigo-700">
              {
                  // Find name by matching same key logic:
                  (() => {
                      const found = projects.find((p) => {
                          const key = p.project_id ?? p.id ?? p._id;
                          return String(key) === String(selectedProject);
                      });
                      return found?.project_name || "";
                  })()
              }
            </span>
                    </p>
                    <button
                        type="button"
                        onClick={() => {
                            setSelectedProject(null);
                            setSelectedSuite("");
                            setExecutedTestcases([]);
                        }}
                        className="text-sm px-3 py-1 bg-red-500 hover:bg-red-600 text-white rounded"
                    >
                        Change Project
                    </button>
                </section>
            )}

            {/* STEP 2: Suite Dropdown */}
            {selectedProject !== null && (
                <section className="mb-4 bg-white p-4 rounded shadow">
                    <div className="flex flex-col md:flex-row gap-4">
                        <div className="flex-1">
                            <label className="block font-medium text-gray-700 mb-1">
                                Suite
                            </label>
                            <select
                                value={selectedSuite}
                                onChange={(e) => {
                                    setSelectedSuite(e.target.value);
                                    setExecutedTestcases([]); // clear old results
                                }}
                                disabled={selectedProject === null}
                                className="w-full border rounded px-3 py-2 mt-1 focus:ring-2 focus:ring-indigo-500 disabled:opacity-50"
                            >
                                <option value="">Select a Suite</option>
                                {suites.map((suite) => (
                                    <option key={suite.suite_id} value={suite.suite_id}>
                                        {suite.name}
                                    </option>
                                ))}
                            </select>

                            {/* Debug: show how many suites loaded */}
                            <p className="mt-1 text-sm text-gray-500">
                                Suites.length = {suites.length}
                            </p>
                        </div>
                    </div>
                </section>
            )}

            {/* SEARCH, STATUS FILTER, EXPORT */}
            {selectedProject !== null && selectedSuite && (
                <div className="flex flex-wrap gap-3 items-center mb-4">
                    <input
                        type="text"
                        placeholder="Search by title"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="border p-2 rounded-md w-full md:w-1/3 text-sm"
                    />

                    <select
                        value={filterStatus}
                        onChange={(e) => setFilterStatus(e.target.value)}
                        className="border p-2 rounded-md w-full md:w-1/4 text-sm"
                    >
                        <option value="">All Statuses</option>
                        <option value="Pass">Pass</option>
                        <option value="Fail">Fail</option>
                    </select>

                    <button
                        type="button"
                        onClick={exportToExcel}
                        className="bg-green-600 text-white px-4 py-2 rounded-md hover:bg-green-700 text-sm"
                    >
                        Export to Excel
                    </button>
                </div>
            )}

            {/* Executed Testcases Table */}
            <div className="bg-white shadow rounded p-4 mt-2 overflow-x-auto">
                <table className="w-full text-sm text-left border">
                    <thead className="bg-gray-200">
                    <tr>
                        <th className="p-2 border">#</th>
                        <th className="p-2 border">Title</th>
                        <th className="p-2 border">Description</th>
                        <th className="p-2 border">Actual Result</th>
                        <th className="p-2 border">Status</th>
                        <th className="p-2 border">Tester</th>
                        <th className="p-2 border">Screenshot</th>
                        <th className="p-2 border">Executed At</th>
                    </tr>
                    </thead>
                    <tbody>
                    {isLoading ? (
                        <tr>
                            <td colSpan="8" className="p-4 text-center">
                                Loading…
                            </td>
                        </tr>
                    ) : filteredTestcases.length === 0 ? (
                        <tr>
                            <td colSpan="8" className="p-2 text-center">
                                No executed testcases found.
                            </td>
                        </tr>
                    ) : (
                        filteredTestcases.map((tc, idx) => (
                            <tr key={tc.id} className="hover:bg-gray-100">
                                <td className="p-2 border">{idx + 1}</td>
                                <td className="p-2 border">{tc.title}</td>
                                <td className="p-2 border">{tc.description}</td>
                                <td className="p-2 border">{tc.actualResult}</td>
                                <td className="p-2 border">
                    <span
                        className={`inline-block px-2 py-1 text-xs font-semibold rounded-full ${
                            tc.status === "Pass"
                                ? "bg-green-100 text-green-800"
                                : "bg-red-100 text-red-800"
                        }`}
                    >
                      {tc.status}
                    </span>
                                </td>
                                <td className="p-2 border">{tc.testerName}</td>
                                <td className="p-2 border">
                                    {tc.screenshot ? (
                                        <a
                                            href={`${process.env.REACT_APP_API_URL.replace(
                                                /\/api$/,
                                                ""
                                            )}${tc.screenshot}`}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="text-indigo-600 hover:underline"
                                        >
                                            View Screenshot
                                        </a>
                                    ) : (
                                        "-"
                                    )}
                                </td>
                                <td className="p-2 border">
                                    {new Date(tc.executedAt).toLocaleString()}
                                </td>
                            </tr>
                        ))
                    )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
